<script lang="ts">
  import { slide } from "svelte/transition";

  /**
   * [TAXONOMY: age_groups]
   *
   * AgeBucket is now ALWAYS loaded from APIs:
   * - Structure: /api/v1/age-buckets/ returns { id, is_child_meal }
   * - Labels: /api/v1/i18n/ returns { age_groups: { baby: { label }, ... } }
   *
   * NO FALLBACK: If APIs fail, ageBuckets will be null and an error is displayed.
   * See ADR 0014 — Alignement Backend/Frontend pour les taxonomies
   */
  type AgeBucket = {
    id: string;
    label: string;
    is_child_meal: boolean;
  };

  type Props = {
    action?: string;
    method?: "get" | "post";

    addGuestLabel?: string;
    confirmLabel?: string;
    removeGuestConfirmMessage?: string;
    accessCodeLabel?: string;
    attendanceLabel?: string;
    attendanceAttendingLabel?: string;
    attendanceNotAttendingLabel?: string;
    contactEmailLabel?: string;
    contactEmailNote?: string;
    successMessage?: string;
    nameLabel?: string;
    ageLabel?: string;
    childcareNote?: string;
    mealLabel?: string;
    mealOptions?: string[];
    dietLabel?: string;
    dietPlaceholder?: string;
    consentText?: string;
    sendingLabel?: string;
    locale?: string;

    /** Age buckets loaded from API. Null if loading failed. */
    ageBuckets?: AgeBucket[] | null;

    /** Localized error message from i18n (errors.loading_failed) */
    loadingErrorMessage?: string | null;

    /** Generic error message for network/unknown errors (i18n) */
    genericErrorMessage?: string;

    /** CSRF token to send in X-CSRF-Token header */
    csrfToken?: string | null;

    /** Reservation payload loaded from access_code link */
    prefillReservation?: PrefillReservationPayload | null;

    /** Error message displayed when prefill failed */
    prefillError?: string | null;

    /** True when an access_code was provided via URL */
    prefillRequested?: boolean;
  };

  type GuestAge = string; // Dynamic based on ageBuckets ids
  type MealChoice = "" | "lamb" | "celeriac" | "child";
  type MealChoiceOption = { id: Exclude<MealChoice, "">; label: string };
  type AttendanceChoice = "" | "attending" | "not_attending";

  type GuestDraft = {
    id: string;
    name: string;
    age: GuestAge;
    mealChoice: MealChoice;
    diet: string;
  };

  type RSVPFormDraft = {
    accessCode: string | null;
    attendance: AttendanceChoice;
    contactEmail: string;
    consent: boolean;
    guests: GuestDraft[];
  };

  type PrefillGuestPayload = {
    name?: string | null;
    age?: string | null;
    meal_choice?: string | null;
    diet?: string | null;
  };

  type PrefillReservationPayload = {
    access_code?: string | null;
    attendance?: string | null;
    contact_email?: string | null;
    consent?: boolean | null;
    guests?: PrefillGuestPayload[] | null;
  };

  const STORAGE_KEY = "rsvp:draft:v2";
  const LEGACY_STORAGE_KEY = "rsvp:draft:v1";
  const GUEST_LIMIT = 10;

  const props: Props = $props();
  const {
    action = "/rsvp",
    method = "post",
    addGuestLabel = "Add new guest",
    confirmLabel = "Confirm guests",
    removeGuestConfirmMessage = "Are you sure you want to remove this guest?",
    attendanceLabel = "Will you attend?",
    attendanceAttendingLabel = "I will attend",
    attendanceNotAttendingLabel = "I cannot attend",
    contactEmailLabel = "Contact email",
    contactEmailNote = "We'll use this email to confirm any updates about your reservation.",
    successMessage = "Thank you! Your RSVP was received.",
    nameLabel = "Full name",
    ageLabel = "Age",
    childcareNote = "A baby-sitter will be on-site.",
    mealLabel = "Dinner choices",
    mealOptions = [],
    dietLabel = "Dietary restrictions or allergies:",
    dietPlaceholder = "E.g., vegetarian, gluten intolerant, etc.",
    consentText = "I agree that my data may be used to manage my attendance.",
    sendingLabel = "Sending...",
    locale,
    ageBuckets,
    loadingErrorMessage,
    genericErrorMessage = "Unable to submit your RSVP. Please try again.",
    csrfToken = null,
    prefillError = null,
  } = props;

  const initialPrefillReservation = props.prefillReservation ?? null;
  const initialPrefillRequested = props.prefillRequested ?? false;

  const defaultMealChoices: MealChoiceOption[] = [
    {
      id: "lamb",
      label: "7-hour slow-cooked lamb in a creamy black garlic sauce",
    },
    {
      id: "celeriac",
      label: "Japanese-style glazed celeriac and crispy chestnut and walnut brittle",
    },
  ];

  function mealChoiceLabels(): MealChoiceOption[] {
    if (mealOptions.length > 0) {
      return [
        { id: "lamb", label: mealOptions[0] },
        {
          id: "celeriac",
          label: mealOptions[1] ?? defaultMealChoices[1].label,
        },
      ];
    }

    return defaultMealChoices;
  }

  /**
   * [TAXONOMY: age_groups]
   * Use injected age buckets from API. NO FALLBACK.
   * If ageBuckets is null/undefined/empty, component shows error.
   */
  const effectiveAgeBuckets = $derived(
    ageBuckets && ageBuckets.length > 0
      ? ageBuckets.map((b) => ({
          id: b.id,
          label: b.label,
          isChildMeal: b.is_child_meal,
        }))
      : null
  );

  /** Whether age buckets failed to load */
  const ageBucketsLoadFailed = $derived(effectiveAgeBuckets === null);

  /** Error message to display when loading fails */
  const displayErrorMessage = $derived(
    loadingErrorMessage || "Loading error. Please refresh the page."
  );

  /**
   * Check if an age value is valid (exists in effectiveAgeBuckets)
   */
  function isValidAge(value: unknown): value is GuestAge {
    if (!effectiveAgeBuckets) return false;
    return typeof value === "string" && effectiveAgeBuckets.some((bucket) => bucket.id === value);
  }

  /**
   * Normalize age value. Returns empty string if invalid.
   */
  function normalizeGuestAge(value: unknown): GuestAge {
    if (isValidAge(value)) return value;
    return "";
  }

  /**
   * Check if an age category requires child meal
   */
  function isChildMealAge(age: GuestAge): boolean {
    if (!age || !effectiveAgeBuckets) return false;
    const bucket = effectiveAgeBuckets.find((entry) => entry.id === age);
    return bucket ? bucket.isChildMeal : false;
  }

  function createGuestId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function createEmptyGuest(): GuestDraft {
    return {
      id: createGuestId(),
      name: "",
      age: "",
      mealChoice: "",
      diet: "",
    };
  }

  function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object";
  }

  function normalizeGuest(value: unknown): GuestDraft | null {
    if (!isRecord(value)) return null;

    const id = typeof value.id === "string" && value.id.length > 0 ? value.id : createGuestId();
    const name = typeof value.name === "string" ? value.name : "";
    const diet = typeof value.diet === "string" ? value.diet : "";

    const age = normalizeGuestAge(value.age);

    let mealChoice: MealChoice =
      value.mealChoice === "lamb" || value.mealChoice === "celeriac" || value.mealChoice === "child" ? value.mealChoice : "";

    if (isChildMealAge(age)) {
      mealChoice = "child";
    } else if (mealChoice === "child") {
      mealChoice = "";
    }

    return {
      id,
      name,
      age,
      mealChoice,
      diet,
    };
  }

  function createEmptyDraft(): RSVPFormDraft {
    return {
      accessCode: null,
      attendance: "attending",
      contactEmail: "",
      consent: false,
      guests: [createEmptyGuest()],
    };
  }

  function loadLegacyGuests(): GuestDraft[] {
    if (typeof window === "undefined") return [createEmptyGuest()];

    try {
      const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!raw) return [createEmptyGuest()];

      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [createEmptyGuest()];

      const guests = parsed
        .map((g) => normalizeGuest(g))
        .filter((g): g is GuestDraft => g !== null)
        .slice(0, GUEST_LIMIT);

      return guests.length > 0 ? guests : [createEmptyGuest()];
    } catch {
      return [createEmptyGuest()];
    }
  }

  function normalizeDraft(value: unknown): RSVPFormDraft | null {
    if (!isRecord(value)) return null;

    const rawAccessCode = typeof value.accessCode === "string" ? value.accessCode.trim().toLowerCase() : "";
    const accessCode = rawAccessCode.length > 0 ? rawAccessCode : null;

    const attendance: AttendanceChoice =
      value.attendance === "attending" || value.attendance === "not_attending" ? value.attendance : "attending";
    const contactEmail = typeof value.contactEmail === "string" ? value.contactEmail : "";
    const consent = typeof value.consent === "boolean" ? value.consent : false;

    const rawGuests = Array.isArray(value["guests"]) ? (value["guests"] as unknown[]) : [];
    const guests = rawGuests
      .map((g) => normalizeGuest(g))
      .filter((g): g is GuestDraft => g !== null)
      .slice(0, GUEST_LIMIT);

    return {
      accessCode,
      attendance,
      contactEmail,
      consent,
      guests: guests.length > 0 ? guests : [createEmptyGuest()],
    };
  }

  function loadDraftFromStorage(): RSVPFormDraft {
    if (typeof window === "undefined") return createEmptyDraft();

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        const normalized = normalizeDraft(parsed);
        if (normalized) return normalized;
      }
    } catch {
      // Ignore parse errors and fall back to legacy format.
    }

    return {
      ...createEmptyDraft(),
      guests: loadLegacyGuests(),
    };
  }

  function normalizeAttendanceFromPrefill(value: unknown): AttendanceChoice {
    if (value === "not_attending") return "not_attending";
    if (value === "attending") return "attending";
    return "attending";
  }

  function buildGuestsFromPrefill(guests: PrefillGuestPayload[] | null | undefined): GuestDraft[] {
    if (!Array.isArray(guests) || guests.length === 0) {
      return [createEmptyGuest()];
    }

    const mapped = guests
      .map((guest) => {
        if (!guest) return null;

        const next = createEmptyGuest();
        next.name = typeof guest.name === "string" ? guest.name : "";

        const rawAge = typeof guest.age === "string" ? guest.age : "";
        next.age = normalizeGuestAge(rawAge);
        next.diet = typeof guest.diet === "string" ? guest.diet : "";

        const rawMeal = typeof guest.meal_choice === "string" ? guest.meal_choice : "";
        let mealChoice: MealChoice =
          rawMeal === "lamb" || rawMeal === "celeriac" || rawMeal === "child" ? rawMeal : "";

        if (isChildMealAge(next.age)) {
          mealChoice = "child";
        } else if (mealChoice === "child") {
          mealChoice = "";
        }

        next.mealChoice = mealChoice;

        return next;
      })
      .filter((guest): guest is GuestDraft => guest !== null)
      .slice(0, GUEST_LIMIT);

    return mapped.length > 0 ? mapped : [createEmptyGuest()];
  }

  function buildDraftFromReservation(prefill: PrefillReservationPayload | null): RSVPFormDraft | null {
    if (!prefill) {
      return null;
    }

    const rawAccessCode = typeof prefill.access_code === "string" ? prefill.access_code.trim().toLowerCase() : "";
    const accessCode = rawAccessCode.length > 0 ? rawAccessCode : null;
    const attendance = normalizeAttendanceFromPrefill(prefill.attendance);
    const contactEmail = typeof prefill.contact_email === "string" ? prefill.contact_email : "";
    const consent = typeof prefill.consent === "boolean" ? prefill.consent : false;
    const guests = buildGuestsFromPrefill(prefill.guests);

    return {
      accessCode,
      attendance,
      contactEmail,
      consent,
      guests,
    };
  }

  function saveDraftToStorage(draft: RSVPFormDraft) {
    if (typeof window === "undefined") return;

    try {
      const snapshot: RSVPFormDraft = {
        accessCode: draft.accessCode,
        attendance: draft.attendance,
        contactEmail: draft.contactEmail,
        consent: draft.consent,
        guests: draft.guests.slice(0, GUEST_LIMIT).map((g) => ({ ...g })),
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // Ignore quota / privacy mode errors.
    }
  }

  const initialDraft = loadDraftFromStorage();
  const prefilledDraft = buildDraftFromReservation(initialPrefillReservation);
  const shouldResetDraft = initialPrefillRequested && !prefilledDraft;
  const baseDraft = shouldResetDraft ? createEmptyDraft() : initialDraft;
  const startingDraft = prefilledDraft ?? baseDraft;

  let guests = $state<GuestDraft[]>(startingDraft.guests);
  let accessCode = $state<string | null>(startingDraft.accessCode);
  let attendance = $state<AttendanceChoice>(startingDraft.attendance);
  let contactEmail = $state<string>(startingDraft.contactEmail);
  let consent = $state<boolean>(startingDraft.consent);
  let isSubmitting = $state(false);
  let submitError = $state<string | null>(null);
  let submitSuccess = $state(false);

  $effect(() => {
    saveDraftToStorage({
      accessCode,
      attendance,
      contactEmail,
      consent,
      guests,
    });
  });

  function setGuestAge(guest: GuestDraft, age: GuestAge) {
    guest.age = age;
    const childMealAge = isChildMealAge(age);
    if (childMealAge) {
      guest.mealChoice = "child";
    } else if (guest.mealChoice === "child") {
      guest.mealChoice = "";
    }
  }

  function buildGuestPayloads(): Array<Record<string, unknown>> {
    let referentAssigned = false;

    return guests
      .map((guest, idx) => {
        const name = guest.name.trim();
        const age = guest.age;
        const mealChoice = guest.mealChoice;
        if (!name || !age || !mealChoice) return null;

        const payload: Record<string, unknown> = {
          position: idx,
          name,
          age,
          meal_choice: mealChoice,
          is_responsible: false,
        };

        if (guest.diet.trim().length > 0) {
          payload["diet"] = guest.diet.trim();
        }

        if (!referentAssigned) {
          payload["is_responsible"] = true;
          referentAssigned = true;
        }

        return payload;
      })
      .filter((guest): guest is Record<string, unknown> => guest !== null);
  }

  async function onSubmit(event: SubmitEvent) {
    const formEl = event.currentTarget as HTMLFormElement | null;
    if (formEl) {
      if (!formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }
    }

    const normalizedEmail = contactEmail.trim();

    const guestsPayload = attendance === "attending" ? buildGuestPayloads() : [];

    const payload: Record<string, unknown> = {
      attendance,
      contact_email: normalizedEmail,
      consent,
      guests: guestsPayload,
      locale,
    };

    if (accessCode && accessCode.trim().length > 0) {
      payload["access_code"] = accessCode.trim().toLowerCase();
    }

    submitError = null;
    submitSuccess = false;
    isSubmitting = true;

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      const normalizedCsrfToken = csrfToken ? csrfToken.trim() : "";
      if (normalizedCsrfToken.length > 0) {
        headers["X-CSRF-Token"] = normalizedCsrfToken;
      }

      const response = await fetch("/api/v1/reservations/", {
        method: "POST",
        headers,
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });

      let data: unknown = null;
      let rawText: string | null = null;

      try {
        rawText = await response.text();
        data = JSON.parse(rawText);
      } catch {
        // JSON parse failed, rawText contains the plain text response
      }

      if (!response.ok) {
        if (data && typeof data === "object" && data !== null) {
          const payload = data as Record<string, unknown>;
          const errorAccessCode = payload["access_code"];
          if (typeof errorAccessCode === "string" && errorAccessCode.trim().length > 0) {
            accessCode = errorAccessCode.trim().toLowerCase();
          } else {
            const errorRsvp = payload["rsvp"];
            if (errorRsvp && typeof errorRsvp === "object") {
              const errorRsvpAccess = (errorRsvp as Record<string, unknown>)["access_code"];
              if (typeof errorRsvpAccess === "string" && errorRsvpAccess.trim().length > 0) {
                accessCode = errorRsvpAccess.trim().toLowerCase();
              }
            }
          }
        }

        let message: string;
        if (data && typeof data === "object" && typeof (data as Record<string, unknown>).error === "string") {
          message = (data as Record<string, unknown>).error as string;
        } else if (rawText && rawText.length < 200) {
          // Use raw text as fallback (e.g., framework error messages like "CSRF token is missing")
          message = rawText;
        } else {
          message = genericErrorMessage;
        }
        submitError = message;
        return;
      }

      if (data && typeof data === "object" && data !== null) {
        const maybeReservation = (data as Record<string, unknown>)["rsvp"];
        if (maybeReservation && typeof maybeReservation === "object") {
          const nextAccessCode = (maybeReservation as Record<string, unknown>)["access_code"];
          if (typeof nextAccessCode === "string" && nextAccessCode.trim().length > 0) {
            accessCode = nextAccessCode.trim().toLowerCase();
          }
        }
      }

      submitSuccess = true;
    } catch {
      submitError = "A network error occurred. Please try again.";
    } finally {
      isSubmitting = false;
    }
  }

  function onAddGuest() {
    if (guests.length >= GUEST_LIMIT) return;
    guests.push(createEmptyGuest());
  }

  function removeGuest(index: number) {
    if (guests.length <= 1) return;
    guests.splice(index, 1);
  }
</script>

{#if ageBucketsLoadFailed}
  <!-- [TAXONOMY: age_groups] Show error when API loading fails - NO FALLBACK -->
  <div class="rsvp-alert rsvp-alert--error" role="alert">
    <p>{displayErrorMessage}</p>
  </div>
{:else}
  <form method={method} action={action} onsubmit={(e) => { e.preventDefault(); void onSubmit(e as unknown as SubmitEvent); }}>

    <label class="form-field form-field__email">
      <span class="form-field__label">{contactEmailLabel}</span>
      <input
        class="form-field__input"
        type="email"
        name="contact_email"
        bind:value={contactEmail}
        required
        >
        <span class="form-field__note">{contactEmailNote}</span>
    </label>

    <input type="hidden" name="attendance" value={attendance || "attending"}>

    {#each guests as guest, i (guest.id)}
      {@const n = i + 1}
      {@const displayName = guest.name.trim().replace(/\s+/g, " ")}

      <fieldset class="rsvp-guest">
        <legend>
          <span class="rsvp-guest-id">{n}.</span>
      	  <!--
          <span class="rsvp-guest-fullname">{displayName ? `${displayName}` : ""}</span>
		  -->
        </legend>

        {#if guests.length > 1}
          <button
            type="button"
            class="rsvp-guest-close"
            aria-label={displayName ? `Remove guest ${n}: ${displayName}` : `Remove guest ${n}`}
            onclick={() => removeGuest(i)}
            >
            <span class="icon" aria-hidden="true"></span>
          </button>
        {/if}

        <label class="form-field form-field__name">
          <span class="form-field__label">{nameLabel}</span>
          <input class="form-field__input" type="text" name={`guest_name_${n}`} bind:value={guest.name} required>
        </label>

        <fieldset class="rsvp-age">
          <legend>{ageLabel}</legend>
          {#if effectiveAgeBuckets}
            {#each effectiveAgeBuckets as bucket}
              <input
                class="radio__input"
                id={`guest_age_${n}-${bucket.id}`}
                type="radio"
                name={`guest_age_${n}`}
                value={bucket.id}
                required
                checked={guest.age === bucket.id}
                onchange={() => setGuestAge(guest, bucket.id)}
                >
                <label class="radio__label" for={`guest_age_${n}-${bucket.id}`}>{bucket.label}</label>
            {/each}
          {/if}
        </fieldset>

        {#if isChildMealAge(guest.age)}
            <div class="rsvp-childcare">
              <p class="rsvp-childcare-note">{childcareNote}</p>
            </div>
        {/if}

        {#if isChildMealAge(guest.age)}
          <input type="hidden" name={`meal_choice_${n}`} value="child">
        {:else}
          <!--start-->
          <fieldset class="rsvp-meal" in:slide={{ duration: 200 }} out:slide={{ duration: 200 }}>
            <legend>{mealLabel}</legend>
            {#each mealChoiceLabels() as choice}
              <input
                class="radio__input"
                id={`meal_choice_${n}-${choice.id}`}
                type="radio"
                name={`meal_choice_${n}`}
                value={choice.id}
                required
                checked={guest.mealChoice === choice.id}
                onchange={() => (guest.mealChoice = choice.id)}
                >
                <label class="radio__label" for={`meal_choice_${n}-${choice.id}`}>
                  {choice.label}
                </label>
            {/each}
          </fieldset>
          <!-- sstop -->
        {/if}

        <label class="form-field">
          <span class="form-field__label">{dietLabel}</span>
          <textarea class="form-field__textarea" name={`guest_diet_${n}`} placeholder={dietPlaceholder} bind:value={guest.diet}></textarea>
        </label>
      </fieldset>
    {/each}

      <button
        type="button"
        class="add-guests"
        onclick={onAddGuest}
        disabled={guests.length >= GUEST_LIMIT || isSubmitting}
        >
        {addGuestLabel}
      </button>

      <label class="rsvp-consent">
        <input type="checkbox" name="rsvp_consent" bind:checked={consent} required>
        <span class="rsvp-consent-text">
          {consentText}
        </span>
      </label>

    <div class="rsvp-actions">
      <button
        type="submit"
        class="confirm-guests"
        class:is-submitting={isSubmitting}
        disabled={isSubmitting}
        >
        {#if isSubmitting}
          {sendingLabel}
        {:else}
          {confirmLabel}
        {/if}
      </button>
    </div>

    {#if prefillError}
      <p class="rsvp-alert rsvp-alert--error" role="alert">{prefillError}</p>
    {/if}
    {#if submitError}
      <p class="rsvp-alert rsvp-alert--error" role="alert">{submitError}</p>
    {/if}
    {#if submitSuccess}
      <div class="rsvp-alert rsvp-alert--success" role="status">
        <p>{successMessage}</p>
      </div>
    {/if}
  </form>
{/if}
